import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { useProcessingStatus } from '@/hooks/use-processing-status';
import { TooltipButton } from '@/comps/tooltip-button';
import { formatNumber } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { formatDateHuman } from '@/utils/date.util';
import { jetbrainsMono } from '@/theme/utils/font.utils';
interface ProcessingStatusIndicatorProps {
  className?: string;
  showLabel?: boolean;
}
const PROCESSING_STATUS_TOOLTIP_CLASS = 'w-fit max-w-none rounded-lg border border-zinc-800 bg-[#141416] px-[13px] py-[11px] shadow-[0_8px_24px_rgba(0,0,0,0.6)] text-left';
function ProcessingStatusTooltipLine(t0) {
  const $ = _c(6);
  const {
    children,
    muted: t1,
    className
  } = t0;
  const muted = t1 === undefined ? false : t1;
  const t2 = muted ? "text-zinc-500" : "text-zinc-200";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("block text-[13px] whitespace-nowrap", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function ProcessingStatusTooltipContent(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className={cn(jetbrainsMono.className, "block text-[10px] tracking-[0.4px] text-zinc-600 uppercase")}>Processing status</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="w-fit text-left">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export const ProcessingStatusIndicator: React.FC<ProcessingStatusIndicatorProps> = t0 => {
  const $ = _c(37);
  const {
    className,
    showLabel: t1
  } = t0;
  const showLabel = t1 === undefined ? false : t1;
  const {
    status,
    rowsInProcessing,
    step,
    lastUpdated
  } = useProcessingStatus();
  let t2;
  if ($[0] !== status) {
    t2 = () => {
      switch (status) {
        case "loading":
          {
            return <Loader2 size={16} className="shrink-0 animate-spin text-muted-foreground" aria-hidden={true} />;
          }
        case "processed":
          {
            return <CheckCircle size={16} className="shrink-0 text-green-500" aria-hidden={true} />;
          }
        case "processing":
          {
            return <Loader2 size={16} className="shrink-0 animate-spin text-yellow-500" aria-hidden={true} />;
          }
        case "waiting_for_processing":
          {
            return <Loader2 size={16} className="shrink-0 animate-spin text-yellow-500" aria-hidden={true} />;
          }
        case "waiting_for_interactions":
          {
            return <AlertTriangle size={16} className="shrink-0 text-gray-500" aria-hidden={true} />;
          }
        case "error":
          {
            return <AlertTriangle size={16} className="shrink-0 text-red-500" aria-hidden={true} />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[0] = status;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getStatusIcon = t2;
  let t3;
  if ($[2] !== lastUpdated || $[3] !== rowsInProcessing || $[4] !== status || $[5] !== step) {
    const getTooltipContent = () => {
      switch (status) {
        case "loading":
          {
            return;
          }
        case "processed":
          {
            return <ProcessingStatusTooltipContent><ProcessingStatusTooltipLine className="mt-[7px]">Status: Completed</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">Last updated: {lastUpdated ? formatDateHuman(lastUpdated) : "N/A"}</ProcessingStatusTooltipLine></ProcessingStatusTooltipContent>;
          }
        case "processing":
          {
            return <ProcessingStatusTooltipContent><ProcessingStatusTooltipLine className="mt-[7px]">Status: Processing</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">Step: {step}</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">Remaining interactions to process: {formatNumber(rowsInProcessing ?? 0)}</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">Last updated: {lastUpdated ? formatDateHuman(lastUpdated) : "N/A"}</ProcessingStatusTooltipLine></ProcessingStatusTooltipContent>;
          }
        case "waiting_for_processing":
          {
            return <ProcessingStatusTooltipContent><ProcessingStatusTooltipLine className="mt-[7px]">Status: Waiting for Processing</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">Remaining interactions to process: {formatNumber(rowsInProcessing ?? 0)}</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">Last updated: {lastUpdated ? formatDateHuman(lastUpdated) : "N/A"}</ProcessingStatusTooltipLine></ProcessingStatusTooltipContent>;
          }
        case "waiting_for_interactions":
          {
            return <ProcessingStatusTooltipContent><ProcessingStatusTooltipLine className="mt-[7px]">Status: Not Started</ProcessingStatusTooltipLine><ProcessingStatusTooltipLine muted={true} className="mt-[3px]">The processing is waiting for interactions</ProcessingStatusTooltipLine></ProcessingStatusTooltipContent>;
          }
        case "error":
          {
            return <ProcessingStatusTooltipContent><ProcessingStatusTooltipLine className="mt-[7px]">Error fetching processing status</ProcessingStatusTooltipLine></ProcessingStatusTooltipContent>;
          }
        default:
          {
            return;
          }
      }
    };
    t3 = getTooltipContent();
    $[2] = lastUpdated;
    $[3] = rowsInProcessing;
    $[4] = status;
    $[5] = step;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const tooltipContent = t3;
  const t4 = showLabel ? "inline-flex h-8 shrink-0 items-center gap-2 whitespace-nowrap border-0 bg-transparent p-0 text-sm font-medium text-muted-foreground" : "flex h-8 w-8 shrink-0 items-center justify-center";
  let t5;
  if ($[7] !== className || $[8] !== t4) {
    t5 = cn(t4, className);
    $[7] = className;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const triggerClassName = t5;
  let t6;
  if ($[10] !== getStatusIcon) {
    t6 = getStatusIcon();
    $[10] = getStatusIcon;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== showLabel) {
    t7 = showLabel ? <span className="whitespace-nowrap">Processing status</span> : null;
    $[12] = showLabel;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const triggerContent = t8;
  if (status === "loading") {
    if (showLabel) {
      let t9;
      if ($[17] !== triggerClassName || $[18] !== triggerContent) {
        t9 = <button type="button" className={triggerClassName} aria-label="Processing status">{triggerContent}</button>;
        $[17] = triggerClassName;
        $[18] = triggerContent;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      return t9;
    }
    let t9;
    if ($[20] !== triggerClassName || $[21] !== triggerContent) {
      t9 = <div className={triggerClassName} aria-label="Processing status indicator">{triggerContent}</div>;
      $[20] = triggerClassName;
      $[21] = triggerContent;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  if (showLabel) {
    const t9 = !!tooltipContent;
    let t10;
    if ($[23] !== triggerClassName || $[24] !== triggerContent) {
      t10 = <button type="button" className={triggerClassName} aria-label="Processing status">{triggerContent}</button>;
      $[23] = triggerClassName;
      $[24] = triggerContent;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== t10 || $[27] !== t9 || $[28] !== tooltipContent) {
      t11 = <TooltipButton content={tooltipContent} delay={400} enabled={t9} align="end" side="top" className={PROCESSING_STATUS_TOOLTIP_CLASS}>{t10}</TooltipButton>;
      $[26] = t10;
      $[27] = t9;
      $[28] = tooltipContent;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    return t11;
  }
  const t9 = !!tooltipContent;
  let t10;
  if ($[30] !== triggerClassName || $[31] !== triggerContent) {
    t10 = <div className={triggerClassName} aria-label="Processing status indicator">{triggerContent}</div>;
    $[30] = triggerClassName;
    $[31] = triggerContent;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t9 || $[35] !== tooltipContent) {
    t11 = <TooltipButton content={tooltipContent} delay={400} enabled={t9} align="end" className={PROCESSING_STATUS_TOOLTIP_CLASS}>{t10}</TooltipButton>;
    $[33] = t10;
    $[34] = t9;
    $[35] = tooltipContent;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
};